import { Chess } from 'chess.js';
import type { DrillPosition } from './types';
import { buildMirroredPosition, type MirroredPosition } from './fenMirror';

/**
 * Pickers in-memory pour les drills Opening Lab — évitent les répétitions
 * consécutives ("ce sont souvent les mêmes exercices qui reviennent" bug
 * Léon 2026-05-17).
 *
 * Principe : chaque drill maintient un `Set<string>` (in `useRef`) avec les
 * `fenBefore` des positions déjà proposées dans la session. Le picker tire
 * un random parmi les non-vus ; quand toutes ont été vues, le Set est reset
 * pour démarrer un nouveau cycle complet (pas de blocage à 0 exercices).
 *
 * Extrait dans son propre module (vs inline dans chaque drill component)
 * parce que (1) ESLint `react-refresh/only-export-components` warn si on
 * exporte une fonction utilitaire depuis un fichier composant, (2) ça
 * permet de tester la logique isolément sans monter le composant React.
 *
 * Persistance : Set vivant pendant le mount du composant — disparaît au
 * refresh/unmount. Acceptable : 30+ positions par répertoire, Léon a peu
 * de chance de toutes les drill en une session. Pour une vraie persistance
 * cross-session, on s'appuiera sur le backend (training_attempts) — déjà
 * fait pour Tactics côté SQL.
 */

/**
 * Tire une position parmi celles non encore proposées dans la session.
 * Si toutes ont été vues → reset le Set pour permettre un nouveau cycle.
 * Mute le Set en place (ajoute la position choisie).
 *
 * @param pool Toutes les positions disponibles pour ce drill.
 * @param seen Set des `fenBefore` déjà proposés (muté en place).
 * @throws Error si pool vide.
 */
export function pickUnseenPosition(
  pool: DrillPosition[],
  seen: Set<string>,
): DrillPosition {
  if (pool.length === 0) {
    throw new Error('No positions to drill');
  }
  let available = pool.filter((p) => !seen.has(p.fenBefore));
  if (available.length === 0) {
    seen.clear();
    available = pool;
  }
  const chosen = available[Math.floor(Math.random() * available.length)]!;
  seen.add(chosen.fenBefore);
  return chosen;
}

/**
 * Variante survival : privilégie les positions profondes (ply >= 8) car
 * plus probable d'être hors-théorie, fallback sur tout le pool si pas
 * assez de positions profondes. Sinon comportement identique à
 * `pickUnseenPosition`.
 */
export function pickUnseenDeepPosition(
  positions: DrillPosition[],
  seen: Set<string>,
): DrillPosition {
  const deep = positions.filter((p) => p.ply >= 8);
  const pool = deep.length > 0 ? deep : positions;
  return pickUnseenPosition(pool, seen);
}

/**
 * Variante trap (DrillPunish) : retourne `null` si pool vide (pas d'erreur),
 * cohérent avec l'API antérieure du composant qui rend un message d'état
 * vide si aucun piège disponible.
 */
export function pickUnseenTrap(
  traps: DrillPosition[],
  seen: Set<string>,
): DrillPosition | null {
  if (traps.length === 0) return null;
  return pickUnseenPosition(traps, seen);
}

/**
 * Variante mirror : tire une position non-vue ET vérifie que son mirror
 * est légal (chess.js peut throw sur certains FEN miroir : doubles rois
 * sur même file, castling rights incompatibles, etc.). Skip silencieux
 * les positions non-mirrorable et essaie une autre dans le bucket courant.
 * Fallback au pool complet si tous les non-vus produisent des mirrors
 * illégaux (rare en pratique).
 */
export interface MirroredDrill {
  original: DrillPosition;
  mirrored: MirroredPosition;
}

export function pickUnseenMirror(
  positions: DrillPosition[],
  seen: Set<string>,
): MirroredDrill | null {
  if (positions.length === 0) return null;
  let primaryPool = positions.filter((p) => !seen.has(p.fenBefore));
  if (primaryPool.length === 0) {
    seen.clear();
    primaryPool = positions;
  }
  const result = tryMirrorFromPool(primaryPool, seen);
  if (result !== null) return result;
  // Fallback : essaie sur le pool complet (au cas où tous les non-vus
  // produisent des mirrors illégaux). Rare en pratique.
  return tryMirrorFromPool(positions, seen);
}

function tryMirrorFromPool(
  pool: DrillPosition[],
  seen: Set<string>,
): MirroredDrill | null {
  const tried = new Set<number>();
  while (tried.size < pool.length) {
    const i = Math.floor(Math.random() * pool.length);
    if (tried.has(i)) continue;
    tried.add(i);
    const original = pool[i]!;
    try {
      const mirrored = buildMirroredPosition(original.fenBefore, original.expectedUci);
      new Chess(mirrored.fen); // valide
      seen.add(original.fenBefore);
      return { original, mirrored };
    } catch {
      continue;
    }
  }
  return null;
}
